// Project management utility functions

import { collection, addDoc, updateDoc, doc, getDocs, deleteDoc, query, where, orderBy, serverTimestamp, getDoc, setDoc } from 'firebase/firestore';

// User preferences utility functions

// Save user visualization preferences
export const saveUserVizPreferences = async (db, user, preferences) => {
  if (!user || !db) {
    console.log('⏭️ Skipping save viz preferences: no user or db');
    return;
  }

  try {
    const userRef = doc(db, 'users', user.uid);
    await setDoc(userRef, {
      vizPreferences: preferences
    }, { merge: true });
    console.log('✅ Saved user visualization preferences:', preferences);
  } catch (error) {
    console.error('❌ Error saving user viz preferences:', error);
  }
};

// Load user visualization preferences
export const loadUserVizPreferences = async (db, user) => {
  if (!user || !db) {
    return null;
  }

  try {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const preferences = userSnap.data().vizPreferences;
      console.log('✅ Loaded user visualization preferences:', preferences);
      return preferences || null;
    }
    return null;
  } catch (error) {
    console.error('❌ Error loading user viz preferences:', error);
    return null;
  }
};

// Save current project ID to user document in Firestore
export const saveCurrentProjectId = async (db, user, projectId) => {
  if (!user) {
    console.log('❌ No user to save current project ID for');
    return;
  }
  try {
    const userRef = doc(db, 'users', user.uid);
    await setDoc(userRef, { currentProjectId: projectId || null }, { merge: true });
    console.log('✅ Saved current project ID:', projectId || 'null');
  } catch (error) {
    console.error('❌ Error saving current project ID:', error);
  }
};

// Load current project ID from user document in Firestore
export const loadCurrentProjectId = async (db, user) => {
  if (!user) {
    console.log('❌ No user to load current project ID for');
    return null;
  }
  try {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const currentProjectId = userSnap.data().currentProjectId;
      console.log('✅ Loaded current project ID:', currentProjectId || 'null');
      return currentProjectId || null;
    }
    return null;
  } catch (error) {
    console.error('❌ Error loading current project ID:', error);
    return null;
  }
};

// Load a project by ID
export const loadProjectById = async (db, projectId) => {
  if (!projectId) {
    return null;
  }
  try {
    const projectRef = doc(db, 'projects', projectId);
    const projectSnap = await getDoc(projectRef);
    if (projectSnap.exists()) {
      return { id: projectSnap.id, ...projectSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('❌ Error loading project by ID:', error);
    return null;
  }
};

// Recursively remove empty string keys from objects (Firestore doesn't allow them)
const removeEmptyStringKeys = (obj) => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeEmptyStringKeys(item));
  }
  
  if (typeof obj !== 'object') {
    return obj;
  }
  
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    // Skip empty string keys
    if (key === '') {
      continue;
    }
    // Recursively clean nested objects and arrays
    cleaned[key] = removeEmptyStringKeys(value);
  }
  return cleaned;
};

// Auto-save optimization results to current project
export const autoSaveOptimizationResults = async (
  db,
  user,
  currentProjectId,
  panels,
  stockSheets,
  kerfThickness,
  labelsOnPanels,
  differentMaterials,
  grainDirection,
  unitSystem,
  optimizationResults
) => {
  // Only auto-save if user is logged in and has a current project
  if (!user || !currentProjectId) {
    console.log('⏭️ Skipping auto-save: no user or no current project');
    return;
  }

  if (!optimizationResults) {
    console.log('⏭️ Skipping auto-save: no optimization results');
    return;
  }

  try {
    // Clean the optimization results to remove empty string keys (Firestore doesn't allow them)
    const cleanedResults = removeEmptyStringKeys(optimizationResults);
    
    // Ensure we're saving the complete optimization results object
    // Firestore can handle nested objects, but we want to make sure the structure is preserved
    const projectData = {
      panels: panels,
      stockSheets: stockSheets,
      settings: {
        kerfThickness: parseFloat(kerfThickness),
        labelsOnPanels,
        differentMaterials,
        grainDirection,
        unitSystem
      },
      lastOptimization: cleanedResults, // Save the cleaned results object
      updatedAt: serverTimestamp()
    };

      await updateDoc(doc(db, 'projects', currentProjectId), projectData);
  } catch (error) {
    console.error('❌ Error auto-saving optimization results:', error);
    console.error('Error details:', error.message, error.stack);
    // Don't show alert for auto-save failures - it's a background operation
  }
};

export const loadUserProjects = async (db, userToLoad, setSavedProjects) => {
  if (!userToLoad) {
    console.log('❌ No user to load projects for');
    return;
  }
  console.log('📂 Loading projects for user:', userToLoad.uid);
  try {
    const projectsQuery = query(
      collection(db, 'projects'),
      where('userId', '==', userToLoad.uid),
      orderBy('updatedAt', 'desc')
    );
    const querySnapshot = await getDocs(projectsQuery);
    const projects = [];
    querySnapshot.forEach((doc) => {
      projects.push({ id: doc.id, ...doc.data() });
    });
    console.log('✅ Loaded projects:', projects.length, projects);
    setSavedProjects(projects);
  } catch (error) {
    console.error('❌ Error loading projects:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    console.error('Full error object:', JSON.stringify(error, null, 2));
    
    // If it's an index error, provide helpful message
    if (error.code === 'failed-precondition' || error.message?.includes('index')) {
      console.error('⚠️⚠️⚠️ FIRESTORE INDEX REQUIRED ⚠️⚠️⚠️');
      console.error('You need to create a composite index in Firestore.');
      console.error('Go to Firebase Console → Firestore Database → Indexes tab');
      console.error('Create a composite index for collection "projects" with:');
      console.error('  - Field: userId (Ascending)');
      console.error('  - Field: updatedAt (Descending)');
      console.error('Or look for an automatic index creation link in the error above.');
    }
    
    // Set empty array so UI doesn't break
    setSavedProjects([]);
  }
};

export const handleSaveProject = async (
  db,
  user,
  saveInputName,
  panels,
  stockSheets,
  kerfThickness,
  labelsOnPanels,
  differentMaterials,
  grainDirection,
  unitSystem,
  optimizationResults,
  currentProjectId,
  originalProjectName,
  setCurrentProjectId,
  setProjectName,
  setOriginalProjectName,
  handleSaveModalClose,
  loadUserProjects
) => {
  if (!user) {
    alert('Please log in to save projects');
    return;
  }
  if (!saveInputName.trim()) {
    alert('Please enter a project name');
    return;
  }


  try {
    // Clean optimization results if they exist (remove empty string keys for Firestore compatibility)
    const cleanedOptimizationResults = optimizationResults ? removeEmptyStringKeys(optimizationResults) : null;
    
    const projectData = {
      userId: user.uid,
      projectName: saveInputName.trim(),
      panels: panels,
      stockSheets: stockSheets,
      settings: {
        kerfThickness: parseFloat(kerfThickness),
        labelsOnPanels,
        differentMaterials,
        grainDirection,
        unitSystem
      },
      lastOptimization: cleanedOptimizationResults,
      updatedAt: serverTimestamp()
    };


    // Check if project name has changed - if so, create new project instead of updating
    const projectNameChanged = currentProjectId && originalProjectName && 
                               saveInputName.trim() !== originalProjectName;

    let finalProjectId = currentProjectId;

    if (currentProjectId && !projectNameChanged) {
      // Update existing project (name hasn't changed)
      await updateDoc(doc(db, 'projects', currentProjectId), projectData);
      // Update the displayed project name
      setProjectName(saveInputName.trim());
      setOriginalProjectName(saveInputName.trim());
    } else {
      // Create new project (either no current project, or name changed)
      if (projectNameChanged) {
      } else {
      }
      projectData.createdAt = serverTimestamp();
      const docRef = await addDoc(collection(db, 'projects'), projectData);
      finalProjectId = docRef.id;
      setCurrentProjectId(finalProjectId);
      setProjectName(saveInputName.trim());
      setOriginalProjectName(saveInputName.trim());
    }

    // Save current project ID to user document
    await saveCurrentProjectId(db, user, finalProjectId);

    handleSaveModalClose();
    // Note: Projects list refresh must be handled by the calling component
  } catch (error) {
    console.error('❌ Error saving project:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
  }
};

export const handleLoadProject = async (
  project,
  setPanels,
  setStockSheets,
  setKerfThickness,
  setLabelsOnPanels,
  setDifferentMaterials,
  setGrainDirection,
  setUnitSystem,
  setOptimizationResults,
  setCurrentProjectId,
  setProjectName,
  setOriginalProjectName,
  handleLoadModalClose,
  db,
  user
) => {
  // Load panels and ensure they have ids arrays
  // Generate sequential IDs for all panel instances (1 to n)
  const loadedPanels = project.panels || [];
  let nextId = 1;
  const panelsWithIds = loadedPanels.map((p) => {
    // If panel already has ids array, use it; otherwise generate new ones
    if (p.ids && Array.isArray(p.ids) && p.ids.length > 0) {
      return p; // Already has ids, use as-is
    }
    
    // Generate new ids array based on quantity
    const quantity = parseInt(p.quantity) || 1;
    const ids = [];
    for (let i = 0; i < quantity; i++) {
      ids.push(nextId++);
    }
    return { ...p, ids };
  });
  setPanels(panelsWithIds);
  setStockSheets(project.stockSheets || []);
  const kerfValue = project.settings?.kerfThickness !== null && project.settings?.kerfThickness !== undefined ? project.settings.kerfThickness.toString() : '0.125';
  setKerfThickness(kerfValue);
  setLabelsOnPanels(project.settings?.labelsOnPanels ?? true);
  setDifferentMaterials(project.settings?.differentMaterials ?? true);
  setGrainDirection(project.settings?.grainDirection ?? true);
  setUnitSystem(project.settings?.unitSystem ?? 'imperial');
  // Load optimization results if they exist and have valid structure
  const loadedResults = project.lastOptimization || null;
  if (loadedResults && loadedResults.stock_sheets && Array.isArray(loadedResults.stock_sheets)) {
    setOptimizationResults(loadedResults);
  } else {
    setOptimizationResults(null);
  }
  setCurrentProjectId(project.id);
  setProjectName(project.projectName);
  setOriginalProjectName(project.projectName); // Track original name
  
  // Save current project ID to user document
  if (db && user) {
    await saveCurrentProjectId(db, user, project.id);
  }
  
  if (handleLoadModalClose) {
    handleLoadModalClose();
  }
};

export const handleDeleteProject = async (
  db,
  user,
  projectId,
  projectName,
  currentProjectId,
  setCurrentProjectId,
  setProjectName,
  setOriginalProjectName,
  loadUserProjects
) => {
  if (!window.confirm(`Are you sure you want to delete "${projectName}"?`)) {
    return;
  }

  try {
    await deleteDoc(doc(db, 'projects', projectId));
    console.log('✅ Project deleted successfully');
    await loadUserProjects(db, user, () => {}); // Pass empty setter since we'll refresh in the calling component
    if (currentProjectId === projectId) {
      setCurrentProjectId(null);
      setProjectName('');
      setOriginalProjectName('');
      // Clear current project ID from user document
      await saveCurrentProjectId(db, user, null);
    }
  } catch (error) {
    console.error('❌ Error deleting project:', error);
  }
};

export const handleNewProject = async (
  currentProjectId,
  setPanels,
  setStockSheets,
  setOptimizationResults,
  setCurrentProjectId,
  setProjectName,
  setOriginalProjectName,
  setEditingProjectName,
  db,
  user
) => {
  if (currentProjectId && window.confirm('Start a new project? Any unsaved changes will be lost.')) {
    setPanels([{ width: '', length: '', quantity: '', materialType: '', label: '', enabled: true, grainDirection: 'either' }]);
    setStockSheets([{ width: '', length: '', quantity: '', materialType: '', enabled: true, grainDirection: 'either' }]);
    setOptimizationResults(null);
    setCurrentProjectId(null);
    setProjectName('');
    setOriginalProjectName('');
    setEditingProjectName(false);
    // Clear current project ID from user document
    if (db && user) {
      await saveCurrentProjectId(db, user, null);
    }
  } else if (!currentProjectId) {
    setPanels([{ width: '', length: '', quantity: '', materialType: '', enabled: true, grainDirection: 'either' }]);
    setStockSheets([{ width: '', length: '', quantity: '', materialType: '', enabled: true, grainDirection: 'either' }]);
    setOptimizationResults(null);
  }
};
